import type {IconType} from "@client/components/Icon";
import * as constants from "@client/constants";
import * as notifications from "@client/features/notifications";
import {getCredentials, getProfile} from "@client/features/orm";
import * as orm from "@client/features/orm";
import {createRouter, routerFactory} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import * as analytics from "@client/shared/analytics";
import {
    AppDispatch,
    RootState,
    store,
    useAppDispatch,
    useAppSelector,
} from "@client/store";
import type {JoyTransitionClass} from "@client/transitions";
import * as utils from "@client/utils";
import {server} from "@reactivated";

type Arr = readonly unknown[];

/* oxlint-disable */
const dispatch: AppDispatch = (...args: any[]) =>
    (store.dispatch as any).apply(null, args);
/* oxlint-enable */

export type PillAction = {
    icon: IconType;
    title: string;
    onClick: () => unknown;
    disabled?: boolean;
    tone?: "danger";
};

export type ToggleOption = {value: string; label: string};

export type CornerToggle = {
    kind: "toggle";
    label: string;
    options: readonly [ToggleOption, ToggleOption];
    value: string;
    onChange: (value: string) => void;
};

declare module "./gotterdammerung" {
    // back      the back button, deleting a workout: the screen slides back
    // forward   Resume, View workout: the workout slides in
    // start     Start: the journal's card travels into the Complete pill
    // complete  Complete: the card comes back out of the pill
    interface Transitions {
        back: true;
        forward: true;
        start: true;
        complete: true;
    }

    // oxlint-disable-next-line typescript/no-empty-object-type
    interface TransitionClasses extends Record<JoyTransitionClass, true> {}

    interface TabDefinition {
        icon: IconType | false;
        title: string;
    }

    interface Options {
        Basic: {
            title: string;
            titleSection?: React.ReactNode;
            isDelegatable?: boolean;
            dialogs?: React.ReactNode;
            topSection?: React.ReactNode;
            bottomSection?: React.ReactNode;
            toolbarYouAreInChargeOfTransparency?: React.ReactNode;
            onRefresh?: () => Promise<unknown>;
            // Currently allows overriding with a custom locator for Stage
            // to be able to go back to Search with the last used tab selected.
            more?: React.ReactNode;
            back?:
                | string
                | {locator: Locator | {linkFromAppToSite: string}; title: string}
                | {title: string; onClick: () => void};
            // One property picks the shell AND carries that shell's knobs, so
            // a knob can never be paired with a shell that ignores it. Only
            // SIMPLE has knobs: `nav` shows the menu button or nothing
            // (replacing the old SIMPLE_NO_MENU string and the fake-close
            // trick that implemented it); `width` caps the header content,
            // body and bottom bar as one column while the band behind the
            // header spans the full window; `surface` is what that full-bleed
            // band paints as content scrolls beneath it — the default fades
            // the page background in under a blur, and a scene on its own
            // background supplies the color the band should melt into.
            // `bottom` swaps the action bar for the journal's floating
            // white pills: one primary pill plus small icon tiles. It
            // cannot share the bar with actions or a bottomSection; route
            // tabs sit in the row beside the pills. `corner`
            // is the header's right slot as a declarative toggle; it and
            // `more` are the same slot, and dark mode's own toggle wins.
            scene?:
                | {
                      kind: "SIMPLE";
                      nav?: "menu" | "none";
                      width?: "column" | "wide";
                      bottom?: {
                          kind: "pills";
                          primary: PillAction | null;
                          tiles?: PillAction[];
                      };
                      corner?: CornerToggle;
                      surface?:
                          | {kind: "fade"}
                          | {
                                kind: "custom";
                                background: string;
                                // Used when the scene respects dark mode and
                                // it is active; falls back to background.
                                darkBackground?: string;
                            };
                  }
                | {kind: "EMPTY"}
                | {kind: "BLANK"}
                | {kind: "MENU_ONLY"}
                | {kind: "FULL"}
                | {kind: "NO_BOTTOM_BORDER"};
            respectDarkMode?: true;
            actionsStyle?: "legacy" | "contrast";
            blank?: never;
        };
    }

    interface Actions {
        Action: {
            // TODO: this needs to be a union here.
            type?: "action";
            track?: string;
            disabled?: boolean;
            onClick?: () => unknown;
            link?: string | Locator;
            icon: IconType;
            title: string;
        };
        Switch: {
            type: "switch";
            onClick: () => unknown;
            icon: IconType;
            title: string;
            isEnabled: boolean;
            highlight?: boolean;
        };
        Empty: null;
    }
}

export function bindDispatch<T extends Arr, R>(
    f: (...args: [...T]) => (dispatch: AppDispatch, getState: () => RootState) => R,
    dispatch: AppDispatch,
) {
    return (...args: T) => dispatch(f(...args));
}

const Anonymous = routerFactory()(() => {
    const profile = useAppSelector(getProfile);
    const dispatch = useAppDispatch();
    const useSelector = useAppSelector;
    const notify = bindDispatch(notifications.notify, dispatch);
    const credentials = useAppSelector(getCredentials);
    const platform = useAppSelector((state) => state.boot.platform);

    if (credentials == null || platform == null) {
        return {
            analytics,
            // oxlint-disable-next-line typescript/no-unsafe-assignment, typescript/no-explicit-any
            credentials: {} as any,
            useSelector,
            profile,
            dispatch,
            notify,
            platform: "web" as const,
        };
    }

    return {
        analytics,
        credentials,
        useSelector,
        profile,
        dispatch,
        notify,
        platform,
    };
})({
    name: "anonymous",
    path: "",
    resolve: async () => {
        const {bootstrap} = await import("@client/features/boot");
        return bootstrap(dispatch, () => store.getState());
    },
});

export const Base = routerFactory()(() => {
    return {
        dispatch: useAppDispatch(),
        profile: useAppSelector(getProfile),
    };
})({
    name: "base",
    path: "",
    resolve: async () => {
        // dispatch({type: "SET_NEXT_ANIMATION", animation: "none"});
        // const {bootstrap} = await import("@client/features/boot");
        // return bootstrap(dispatch, () => store.getState());
    },
});

export const StoreUpdateRequired = Anonymous.subroute({
    name: "store_update_required",
    path: "store-update-required/",
});

export const Maintenance = Anonymous.subroute({
    name: "maintenance",
    path: "maintenance/",
});

export const Login = Base.subroute({
    name: "login",
    path: "login/",
});

export const PlanOnboarding = Base.subroute({
    name: "plan_onboarding",
    path: "plan/",
    resolve: async () => {
        return {
            joyTag: await hardware.getTag(),
        };
    },
});

export const WalkingClub = Base.subroute({
    name: "walking_club",
    path: "club/",
    resolve: async () => {
        const demo = await server.journal.clubs.walking_club_demo_allowed({
            ignored: "",
        });
        const board = await server.journal.clubs.walking_club_board({
            ignored: "",
        });
        return {
            demo: demo.type === "success" ? demo.data : false,
            board: board.type === "success" ? board.data : null,
        };
    },
});

export const WalkingClubJoin = WalkingClub.subroute({
    name: "walking_club_join",
    path: "<string:code>/",
    resolve: async (props) => {
        const demo = await server.journal.clubs.walking_club_demo_allowed({
            ignored: "",
        });
        const info = await server.journal.clubs.walking_club_info({
            code: props.params.code,
        });
        return {
            demo: demo.type === "success" ? demo.data : false,
            info: info.type === "success" ? info.data : null,
        };
    },
});

export const PasswordReset = Anonymous.subroute({
    name: "reset",
    path: "reset/",
});

export const SetPassword = Anonymous.subroute({
    name: "set_password",
    path: "reset/<string:uidb64>/<string:token>/",
});

export const MagicLinkLogin = Anonymous.subroute({
    name: "magic_link_login",
    path: "reset/login/",
});

export const Registration = Anonymous.subroute({
    name: "register",
    path: "register/",
    resolve: async () => {
        const demo = await server.journal.clubs.walking_club_demo_allowed({
            ignored: "",
        });
        return {
            email: null as string | null,
            demo: demo.type === "success" ? demo.data : false,
        };
    },
});

export const Contact = Anonymous.subroute({
    name: "contact",
    path: "contact/",
    tabs: ["support", "manager", "feedback"],
});

export const Share = Anonymous.subroute({
    name: "share",
    path: "share/<string:share_uuid>/",
    resolve: async (props) => {
        // A walking club's deep link rides the share namespace (only /share/*
        // universal links open the installed app), addressed by join_code as
        // the slug. The scene redirects it into the join flow.
        const club = await server.journal.clubs.walking_club_info({
            code: props.params.share_uuid,
        });
        if (club.type === "success" && club.data != null) {
            return {
                type: "walking_club" as const,
                share_uuid: props.params.share_uuid,
            };
        }

        const result = await server.journal.nutrition.share_detail({
            share_uuid: props.params.share_uuid,
        });

        if (result.type != "success") {
            // A thrown resolve aborts the transition and blank-screens a
            // deep-linked app — render the dead-link state instead.
            return {
                type: "missing" as const,
                share_uuid: props.params.share_uuid,
            };
        }

        return result.data;
    },
});

export const ShareDebug = Anonymous.subroute({
    name: "share_debug",
    path: "share/debug/<string:share_uuid>/",
});

export const Authenticated = routerFactory()(() => {
    const profile = useAppSelector(getProfile);
    const dispatch = useAppDispatch();
    const notify = bindDispatch(notifications.notify, dispatch);
    const useSelector = useAppSelector;
    const platform = useAppSelector((state) => state.boot.platform);
    const deviceUUID = useAppSelector((state) => state.boot.deviceUUID)!;

    if (profile == null) {
        return Login.reverse({});
    }

    return {
        analytics,
        profile,
        useSelector,
        currentlyManagedUserId: profile.user.id,
        dispatch,
        notify,
        platform,
        deviceUUID,
    };
})({
    name: "authenticated",
    path: "",
    resolve: async () => {
        const {bootstrap} = await import("@client/features/boot");
        return bootstrap(dispatch, () => store.getState());
    },
});

export const Today = Authenticated.subroute({
    name: "today",
    path: "journal/",
    tabs: ["journal", "workout", "summary", "group", "profile"],
});

export const Journal = Authenticated.subroute({
    name: "journal",
    path: "journal/<string:key>/",
    tabs: ["journal", "workout", "summary", "group", "profile"],
    resolve: async (props) => {
        const currentlyManagedUserId = store.getState().orm.profile?.user.id;

        if (currentlyManagedUserId == null) {
            // oxlint-disable-next-line typescript/only-throw-error
            throw "unauthorized";
        }

        // Circular imports
        const result = await server.journal.day.journal({
            key: props.params.key,
            user_id: currentlyManagedUserId,
        });

        if (result.type != "success") {
            throw new Error("Whoops new");
        }

        return result.data;
    },
});

export const CheckIn = Journal.subroute({
    name: "check_in",
    path: "check-in/",
});

export const UpdateMeasurements = Journal.subroute({
    name: "update_measurements",
    path: "measurements/",
});

export const UpdateJournalEntry = Journal.subroute({
    name: "update_journal_entry",
    path: "entries/<string:entryUUID>/",
    tabs: ["macros", "recipe"],
});

export const MyFitnessPal = Journal.subroute({
    name: "myfitnesspal",
    path: "mfp/",
});

export const UpdateMeal = Journal.subroute({
    name: "update_meal",
    path: "meals/<string:mealUUID>/",
    resolve: (props) => {
        const meal = props.resolved.journal.day.meals.find(
            (meal) => meal.uuid == props.params.mealUUID,
        )!;
        return meal;
    },
});

export const CreateRecipeFromMeal = UpdateMeal.subroute({
    name: "create_recipe_from_meal",
    path: "recipe/",
});

export const JournalContact = Journal.subroute({
    name: "journal_contact",
    path: "contact/",
    tabs: ["support", "manager", "feedback"],
});

export const RecipeList = Authenticated.subroute({
    name: "recipe_list",
    path: "recipes/",
    resolve: async () => {
        const result = await server.journal.nutrition.recipe_list({});

        return result.type == "success" ? result.data : [];
    },
});

export const CreateRecipe = RecipeList.subroute({
    name: "create_recipe",
    path: "create/",
    tabs: ["index", "import"],
});

export const UpdateRecipe = RecipeList.subroute({
    name: "update_recipe",
    path: "<number:recipeId>/",
    tabs: ["recipe", "details"],
    resolve: ({params, resolved}) =>
        resolved.recipe_list.find((recipe) => recipe.id === params.recipeId)!,
});

export const UpdateRecipeEntry = UpdateRecipe.subroute({
    name: "update_recipe_entry",
    path: "entries/<string:entryUUID>/",
    tabs: UpdateJournalEntry.definition.tabs,
});

export const FoodList = Authenticated.subroute({
    name: "food_list",
    path: "foods/",
    resolve: async () => {
        const result = await server.journal.nutrition.food_list({});

        return result.type === "success" ? result.data : [];
    },
});

export const CreateFood = FoodList.subroute({
    name: "create_food",
    path: "create/",
    tabs: ["macros", "advanced"],
});

export const UpdateFood = FoodList.subroute({
    name: "update_food",
    path: "<number:foodId>/",
    tabs: CreateFood.definition.tabs,
    resolve: ({params, resolved}) =>
        resolved.food_list.find((food) => food.id === params.foodId)!,
});

export const Profile = Authenticated.subroute({
    name: "profile",
    path: "profile/",
    tabs: ["profile", "subscription", "manager", "settings"],
    resolve: async () => {
        await dispatch(orm.fetchSubscription());

        return {};
    },
});

export const Manager = Authenticated.subroute({
    name: "manager",
    path: "manager/",
    resolve: async () => {
        const url = await server.core.rpc.get_client_dashboard_link();

        const {requester} = await import("@client/features/boot");
        const result = await requester("/clients/", null, "GET");

        if (result.type != "success" || url.type != "success") {
            throw new Error("Cannot happen");
        }
        return {
            dashboard: result.data as {
                props: server.manager.templates.ClientDashboard;
            },
            url: url.data,
        };
    },
});

export const Planner = Authenticated.subroute({
    name: "planner",
    path: "planner/",
});

export const Calculator = Anonymous.subroute({
    name: "calculator",
    path: "calculator/",
});

export const Macros = Authenticated.subroute({
    name: "macros",
    path: "macros/",
    resolve: async () => {
        const demo = await server.journal.clubs.walking_club_demo_allowed({
            ignored: "",
        });
        return {
            demo: demo.type === "success" ? demo.data : false,
        };
    },
});

export const Tutorials = Anonymous.subroute({
    name: "tutorials",
    path: "tutorials/",
    // Something funky with the covers happens where they don't load for a while
    // even on a fast connection. Could be because the browser is busy preloading
    // all the videos.
    //
    // So we preload them all before even entering the scene.
    resolve: async () => {
        const staticURL = utils.getStaticBucketURL();

        await Promise.all(
            constants.TUTORIALS.map(([, tutorial]) => {
                return new Promise<void>((resolve) => {
                    const image = new Image();
                    image.src = `${staticURL}videos/cover-${tutorial}.mov.gif`;
                    image.onload = () => resolve();
                    image.onerror = () => {
                        // Log error or handle, but still resolve so Promise.all doesn't reject
                        console.error(`Failed to preload image: ${image.src}`);
                        resolve();
                    };
                });
            }),
        );
    },
});

export const Strength = Anonymous.subroute({
    name: "strength",
    path: "strength-standards/",
    resolve: async () => {
        const result = await server.journal.strength.strength();
        if (result.type !== "success")
            throw new Error("Could not load strength standards");
        return result.data;
    },
});

export const Peptides = Anonymous.subroute({
    name: "peptides",
    path: "peptides/",
    tabs: ["calculator", "vials"],
    resolve: async () => {
        const result = await server.journal.peptides.peptides();

        return result.type === "success" ? result.data : {vials: []};
    },
});

export const PeptideDetail = Anonymous.subroute({
    name: "peptide_detail",
    path: "peptides/<string:slug>/",
});

export const Reports = Authenticated.subroute({
    name: "reports",
    path: "reports/",
    tabs: ["measurements", "macros", "exercises"],
});

export const InviteUser = Authenticated.subroute({
    name: "invite_user",
    path: "invite/",
});

export const ConfirmPayPal = Authenticated.subroute({
    name: "confirm_paypal",
    path: "subscription/paypal/",
});

export const Migrate = Authenticated.subroute({
    name: "migrate",
    path: "migrate/",
    resolve: async () => {
        if (store.getState().orm.profile?.user.id == null) {
            // oxlint-disable-next-line typescript/only-throw-error
            throw "unauthorized";
        }

        const result = await server.journal.mfp.mfp_migration_detail();

        if (result.type != "success") {
            throw new Error("Cannot happen");
        }
        return result.data;
    },
});

export const Workouts = Authenticated.subroute({
    name: "workouts",
    path: "workouts/",
    tabs: ["workouts", "program", "exercises"],
    resolve: async () => {
        const result = await server.journal.workouts.workouts();

        if (result.type == "unauthorized") {
            // oxlint-disable-next-line typescript/only-throw-error
            throw "unauthorized";
        }

        if (result.type != "success") {
            throw new Error("Cannot happen");
        }

        return result.data;
    },
});

export const CreateWorkout = Workouts.subroute({
    name: "create_workout",
    path: "create/",
});

export const CreateWorkoutProgram = Anonymous.subroute({
    name: "create_workout_program",
    path: "workouts/program/create/",
    resolve: async () => {
        const result = await server.journal.workouts.workouts();

        if (result.type != "success") {
            throw new Error("Cannot happen");
        }

        return result.data;
    },
});

export const UpdateWorkoutTemplate = Workouts.subroute({
    name: "update_workout_template",
    path: "<number:workoutId>/",
    tabs: ["workout", "details"],
    resolve: ({params, resolved}) =>
        resolved.workouts.templates.find(
            (template) => template.id === params.workoutId,
        )!,
});

export const UpdateExerciseType = Workouts.subroute({
    name: "update_exercise_type",
    path: "exercises/<number:exerciseTypeId>/",
    resolve: async ({params, resolved}) => {
        const exerciseType = resolved.workouts.exercise_types.find(
            (exercise) => exercise.id === params.exerciseTypeId,
        )!;
        return {
            exerciseType,
            version: await hardware.getAppInfo(),
        };
    },
});

export const Storyboard = Authenticated.subroute({
    name: "storyboard",
    path: "storyboard/",
});

export const Logout = Authenticated.subroute({
    name: "logout",
    path: "logout/",
});

export const {
    Link,
    unsafeTransitionByUrl,
    mount,
    reverse,
    transition,
    push,
    replace,
    back,
    locator,
    getLocatorFromURL,
} = createRouter(
    [
        Base,
        StoreUpdateRequired,
        Maintenance,
        Storyboard,
        Tutorials,
        Peptides,
        Strength,
        PeptideDetail,
        Share,
        ShareDebug,
        Login,
        PlanOnboarding,
        WalkingClub,
        WalkingClubJoin,
        Registration,
        PasswordReset,
        MagicLinkLogin,
        SetPassword,
        Today,
        Journal,
        CheckIn,
        UpdateMeasurements,
        Contact,
        JournalContact,
        UpdateJournalEntry,
        MyFitnessPal,
        UpdateMeal,
        CreateRecipeFromMeal,
        FoodList,
        CreateFood,
        UpdateFood,
        RecipeList,
        Workouts,
        CreateWorkout,
        UpdateWorkoutTemplate,
        UpdateExerciseType,
        CreateWorkoutProgram,
        CreateRecipe,
        UpdateRecipe,
        UpdateRecipeEntry,
        Planner,
        Calculator,
        Macros,
        Profile,
        Reports,
        Manager,
        InviteUser,
        ConfirmPayPal,
        Migrate,
        Logout,
    ],
    "browser",
    {
        pathRewrite: (url) => {
            const host = url.hostname.replace(/^www\./, "");
            if (
                server.core.constants.PEPTIDE_ALIAS_DOMAINS.some(
                    (domain) => domain === host,
                ) &&
                url.pathname === "/"
            ) {
                return "/peptides/";
            }
            return url.pathname;
        },
    },
);
